import PageLayout from "@/components/feature/PageLayout";
import CTAButton from "@/components/feature/CTAButton";
import { activeSeason } from "@/mocks/activeSeason";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { getActiveSeason } from "@/services/content";
import { fallbackPracticeData } from "@/mocks/practices";
import { getPracticeData, liveSpecials } from "@/services/practices";
import { useRef, useEffect } from "react";

export default function Home() {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Falls back to src/mocks/activeSeason.ts until Supabase is wired up —
  // see README-ADRIAN.md.
  const { data: season } = useSupabaseData(getActiveSeason, activeSeason);
  // "Tryout Schedule" while tryouts are on the practices page; back to "Schedule" on its own after.
  const { data: practices } = useSupabaseData(getPracticeData, fallbackPracticeData);
  const tryoutsLive = liveSpecials(practices.specials).some((s) => /tryout/i.test(s.blockTitle));

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.defaultMuted = true;
      v.setAttribute("muted", "");
      v.playsInline = true;
      v.setAttribute("webkit-playsinline", "true");
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, []);


  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative w-full min-h-[600px] md:min-h-[800px] flex items-start justify-center overflow-hidden pt-24 md:pt-32">
        <div className="absolute inset-0 overflow-hidden">
          <video
            ref={videoRef}
            src="https://mbqtdfdhqbpbcfqzdbso.supabase.co/storage/v1/object/public/site-assets/triumph_hero_5s.mp4"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: "scale(1.4)" }}
            autoPlay
            muted
            playsInline
            preload="auto"
            webkit-playsinline="true"
          />

        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65 z-10"></div>
        <div className="relative z-20 w-full px-4 md:px-8 lg:px-12 text-center">
          <img
            src="https://mbqtdfdhqbpbcfqzdbso.supabase.co/storage/v1/object/public/site-assets/triumph-logo-header.png"
            alt="Triumph Basketball"
            className="h-[4.5rem] w-auto md:h-[6.25rem] mx-auto mb-6 drop-shadow-lg"
          />
          <div className="inline-flex items-center gap-2 bg-strike-500/10 border border-strike-500/30 px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-strike-500 inline-block"></span>
            <span className="font-body text-strike-500 text-xs font-semibold tracking-[0.15em] uppercase">
              Official Under Armour Program
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight leading-none">
            {season.name} Season
          </h1>
          <p className="font-body text-white/60 text-sm md:text-base tracking-widest uppercase mt-4 mb-8">
            {season.dateRange}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <CTAButton variant="primary" size="lg" to="/programs">
              Register Now
            </CTAButton>
            <CTAButton variant="outline" size="lg" to="/practices">
              {tryoutsLive ? "Tryout Schedule" : "Schedule"}
            </CTAButton>
          </div>
        </div>
      </section>

      {/* What Is Triumph */}
      <section className="w-full px-4 md:px-8 lg:px-12 -mt-16 md:-mt-28 pt-8 md:pt-12 pb-16 md:pb-24 bg-onyx-50 relative z-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left — text */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-0.5 bg-strike-500"></div>
                <span className="font-body text-strike-500 text-xs font-semibold tracking-[0.2em] uppercase">
                  Established Texas
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight leading-tight mb-6">
                Triumph Basketball
              </h2>
              <p className="font-body text-white/60 text-sm md:text-base leading-relaxed mb-6">
                Triumph Basketball is one of Dallas&apos; premier youth basketball programs, serving boys from Kindergarten through High School, including our Elite UA Rise teams. Girls play with our sister club, Athletes First. Est. 2007 · Dallas, Texas. As an Official Under Armour Program, we believe every athlete deserves the opportunity to grow and develop their full potential.
              </p>
              <p className="font-body text-white/60 text-sm md:text-base leading-relaxed mb-6">
                Our mission is simple: develop complete basketball players. Through high-level coaching, structured practices, and competitive game experiences, athletes build strong fundamentals, basketball IQ, confidence, discipline, and the mental toughness needed to succeed both on and off the court.
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="bg-gunmetal-50 border border-onyx-400 px-5 py-3">
                  <span className="font-heading text-strike-500 text-2xl font-bold block">
                    2020
                  </span>
                  <span className="font-body text-white/40 text-xs uppercase tracking-wider">
                    Under Armour Partner
                  </span>
                </div>
                <div className="bg-gunmetal-50 border border-onyx-400 px-5 py-3">
                  <span className="font-heading text-strike-500 text-2xl font-bold block">
                    K-12
                  </span>
                  <span className="font-body text-white/40 text-xs uppercase tracking-wider">
                    Grades Served
                  </span>
                </div>
              </div>
              <div className="mt-6">
                <CTAButton variant="primary" size="md" to="/programs">
                  Our Programs
                </CTAButton>
              </div>
            </div>

            {/* Right — image + programs */}
            <div className="flex flex-col gap-6">
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src="https://mbqtdfdhqbpbcfqzdbso.supabase.co/storage/v1/object/public/site-assets/Gemini_Generated_Image_335aih335aih335a.webp"
                  alt="Triumph Basketball Training"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx-50/80 via-onyx-50/20 to-transparent"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { team: "Boys 2nd–8th Grade", color: "strike" },
                  { team: "Boys High School", color: "strike" },

                  { team: "UA Rise Elite", color: "strike" },
                ].map(({ team, color }) => (
                  <div
                    key={team}
                    className="flex items-center gap-3 bg-gunmetal-50 border border-onyx-400 px-4 py-3"
                  >
                    <div className={`w-6 h-6 flex items-center justify-center ${color === "strike" ? "bg-strike-500/10" : "bg-white/10"}`}>
                      <i className={`ri-basketball-line ${color === "strike" ? "text-strike-500" : "text-white"} text-xs`}></i>
                    </div>
                    <span className="font-body text-white/80 text-sm font-medium">
                      {team}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}